/**
 * One row in the chat-history listing (§4.2/§4.5/§4.6) — open, inline-rename, and
 * delete-with-confirm for a single thread. Ported from the standalone reference FE's
 * `ThreadSidebar.tsx` `ThreadRow` so the sidebar/history experience matches: a pencil
 * reveals an inline-editable title (Enter/Escape/blur commits), and delete requires an
 * explicit confirm tap (check/close) rather than deleting on the first click.
 */
import React, { useState } from 'react';
import { Icon, FONT, relativeTime, absoluteTime, shortStamp } from './atoms';
import type { PCConversationSummary } from '../../../interface/ProductCatalogChatbotInterface';

export const PCThreadRow: React.FC<{
    thread: PCConversationSummary;
    onOpen: () => void;
    onRename: (title: string) => void;
    onDelete: () => void;
}> = ({ thread, onOpen, onRename, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(thread.title);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const commitRename = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed && trimmed !== thread.title) onRename(trimmed);
        else setDraft(thread.title);
    };

    return (
        <div
            onClick={() => !editing && onOpen()}
            style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 16px', borderBottom: '1px solid var(--divider)',
                cursor: editing ? 'default' : 'pointer',
            }}
        >
            <Icon name="chat_bubble" size={18} color="var(--t3)" />
            <div style={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                    <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') { setDraft(thread.title); setEditing(false); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            fontSize: 13, fontFamily: FONT, color: 'var(--t1)', background: 'var(--surface)',
                            border: '1px solid var(--accent)', borderRadius: 6, padding: '3px 6px', outline: 'none', width: '100%',
                        }}
                    />
                ) : (
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {thread.title || 'New chat'}
                    </div>
                )}
                {/* When it last happened, then how big it is — the timestamp is what people scan
                    a history list by, so it leads. Relative for quick reading, with the exact
                    moment on hover so the precise time is never lost. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--t3)', marginTop: 2, flexWrap: 'wrap' }}>
                    <span title={absoluteTime(thread.updatedAt)}>{relativeTime(thread.updatedAt, '')}</span>
                    <span style={{ opacity: 0.5 }}>·</span>
                    {/* The real clock time, always visible — a tooltip needs a mouse, so on touch
                        the timestamp was simply unreachable. Today's rows show the time alone,
                        since the day heading above already says which day it was. */}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{shortStamp(thread.updatedAt)}</span>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span>{thread.messageCount} message{thread.messageCount === 1 ? '' : 's'}</span>
                </div>
            </div>

            {!editing && (
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                    {confirmingDelete ? (
                        <>
                            <button
                                type="button" title="Confirm delete"
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--red)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="check" size={16} />
                            </button>
                            <button
                                type="button" title="Cancel"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="close" size={16} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button" title="Rename"
                                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="edit" size={16} />
                            </button>
                            <button
                                type="button" title="Delete chat"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="delete" size={16} />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
